/**
 * Textos del juego en espanol.
 *
 * Un fichero por idioma, y NO los cuatro juntos: los cuatro juntos iban en la
 * primera carga, y el jugador pagaba el peso de tres idiomas que no lee. Solo
 * el espanol entra de serie, porque es el que manda cuando falta cualquier
 * otra cosa; los demas los trae `i18n/index.ts` con un `import()`.
 *
 * Los textos llevan tildes y enes de verdad (regla de la casa). El fichero es
 * UTF-8 y vite lo mete en el trozo tal cual.
 */

import { numero } from './numero';
import type { Textos } from './tipos';

export const textos: Textos = {
  jugar: 'Jugar',
  record: 'Récord',
  monedas: 'Monedas',
  duelos: 'Duelos',
  clan: 'Clan',
  ranking: 'Ranking',
  pozo: 'Pozo',
  tareas: 'Tareas',
  referidos: 'Invitar',
  tienda: 'Tienda',
  perfil: 'Perfil',

  metros: 'm',
  combo: 'combo',

  finPartida: 'Fin de la partida',
  distancia: 'Distancia',
  monedasGanadas: 'Monedas',
  nuevoRecord: '¡Nuevo récord!',
  casiLoLogras: '¡Casi lo logras!',
  casiDetalle: (faltaban) => `Te faltaban ${faltaban} m para tu récord`,
  otraVez: 'Otra vez',
  revivirConAnuncio: 'Revivir (ver anuncio)',
  compartirRecord: 'Compartir récord',
  volverInicio: 'Inicio',

  revivir: 'Seguir jugando',
  revivirRestantes: (n) => (n === 1 ? 'Te queda 1' : `Te quedan ${n}`),
  noRevivir: 'Terminar la partida',
  revivirPorMonedas: (n) => `Seguir jugando por ${numero(n)} monedas`,
  sinMasRevivires: 'Ya has revivido 3 veces en esta partida',
  jugarConX2: 'Jugar con monedas ×2',
  x2Listo: '×2 listo para tu próxima partida',
  x2Activo: 'Monedas ×2 activas',
  cajaDiaria: (monedas) => `Caja diaria: ${monedas} monedas`,
  cajaDiariaUsada: 'Ya has abierto la caja de hoy. Vuelve mañana.',
  cajaAbierta: (monedas) => `¡+${monedas} monedas!`,
  cargandoAnuncio: 'Cargando el anuncio…',
  anuncioDePrueba: 'Anuncio de prueba',
  anuncioPruebaPie:
    'Esto es una simulación para poder probar el juego. No es un anuncio real y no genera ningún ingreso.',
  anuncioNoCompletado: 'No has visto el anuncio completo, así que no hay recompensa.',
  anuncioSinConfirmar:
    'No he podido confirmar el anuncio. Si lo viste entero, inténtalo otra vez.',
  anuncioNoDisponible: 'Ahora mismo no hay anuncios disponibles.',

  retar: 'Retar a un amigo',
  aceptarReto: 'Aceptar reto',
  teReta: (quien, metros) => `${quien} te reta: supera ${numero(metros)} m 🚀`,
  supera: (metros) => `Supera ${numero(metros)} m`,
  duelosGanados: 'Duelos ganados',
  rachaVictorias: 'Racha',

  cargando: 'Cargando…',
  sinConexion: 'Sin conexión. Revisa tus datos y vuelve a intentarlo.',
  errorGenerico: 'Algo salió mal. Inténtalo de nuevo.',
  pausado: 'Pausa',
  continuar: 'Continuar',

  legalPuntosTemporada:
    'Los puntos de temporada no tienen valor monetario y podrían tener utilidad futura dentro del ecosistema.',

  biomaCiudad: 'Ciudad',
  biomaEstratosfera: 'Estratosfera',
  biomaEspacio: 'Espacio',

  miPersonaje: 'Mi personaje',
  avatarCuerpo: 'Cuerpo',
  avatarChico: 'Chico',
  avatarChica: 'Chica',
  avatarPeinado: 'Peinado',
  avatarPelo: 'Color de pelo',
  avatarPiel: 'Tono de piel',
  avatarTraje: 'Ropa',
  avatarGuardar: 'Guardar',
  avatarGuardado: '¡Guardado!',
  avatarError: 'No se pudo guardar. Inténtalo otra vez.',
  peinadoRapado: 'Rapado',
  peinadoCorto: 'Corto',
  peinadoRizado: 'Rizado',
  peinadoColeta: 'Coleta',
  peinadoMelena: 'Melena',
  peinadoMono: 'Moño',
  trajeBloqueadoInvitado: 'Se desbloquea al entrar con la invitación de un amigo',
  trajeBloqueadoReclutador: 'Se desbloquea cuando 3 de tus invitados se activen',
  trajeBloqueadoPase: 'Es del Pase de Temporada, que todavía no está a la venta',
  tutorialTitulo: 'Cómo se juega',
  tutorialCarril: 'Desliza a los lados para cambiar de carril',
  tutorialSaltar: 'Toca o desliza arriba para saltar',
  tutorialAgachar: 'Desliza abajo para agacharte',
  tutorialVamos: '¡Vamos!',
};

/**
 * Textos sueltos: los que se anaden despues de las pantallas base.
 *
 * Van aparte del objeto `Textos` para no obligar a traducir los cuatro
 * idiomas cada vez que aparece una pantalla. Aqui el espanol manda y el resto
 * cae en el si le falta una clave.
 */
export const sueltos: Record<string, string> = {
  retar: '⚔️ Retar a un amigo',
  creandoReto: 'Preparando el reto…',
  juegaTuReto: 'Juega tu marca y luego compártela',
  compartirReto: '📤 Compartir el reto',
  retoListo: '¡Reto listo!',
  retoListoDetalle: 'Compártelo y que intenten superarte',
  sinSaldo: 'No tienes monedas suficientes para apostar',
  duelosApagados: 'Los duelos no están disponibles ahora mismo',
  doblarPremio: '×2 al premio',
  invitaTexto: '¡Juega conmigo a MoonRush! ¿Hasta dónde llegas? 🚀',
  compartir: '📤 Compartir',
  enlaceCopiado: '✓ Enlace copiado',
  retoRecibido: '⚔️ Te han retado',
  ahoraNo: 'Ahora no',
  fichas: 'fichas',
  cuantoApuestas: '¿Cuánto apuestas?',
  sinApuesta: 'Sin apuesta',
  miClan: 'Mi clan',
  clanMiembros: 'Miembros',
  clanTotal: 'Entre todos',
  clanSalir: 'Salir del clan',
  clanVasElPuesto: 'Vas el',
  clanSinClan: 'Todavía no estás en ningún clan',
  clanComoSeCrea: 'Un clan es un grupo de Telegram. Añade el bot a tu grupo y escribe /clan dentro: te dará un enlace para que entren los demás.',
  clanVacio: 'Nadie del clan ha corrido todavía',
  tienes: 'Tienes',
  rivalPoneIgual: 'Quien acepte pone lo mismo. Si el reto caduca sin rival, se te devuelve.',
  aceptando: 'Aceptando el reto…',
  mismoNivel: 'Jugáis el mismo nivel: mismos obstáculos, mismos sitios',
  apuestaEnJuego: 'En juego',
  retoNoDisponible: 'Ese reto ya no está disponible',
  retoSinSaldo: 'No tienes monedas suficientes para aceptarlo',

  // --- La pantalla del pozo (fase 8) ---
  pozoTitulo: 'El pozo',
  pozoExplica: 'Cada metro que corres suma un punto. Al acabar la semana, el pozo se reparte entre quien ha jugado de verdad.',
  pozoPuntos: 'Puntos',
  pozoPartidas: 'Partidas válidas',
  pozoFaltan: 'Te faltan {n} partidas válidas para entrar en el reparto',
  pozoDentro: '✓ Ya estás dentro del reparto de esta semana',
  pozoMonedero: 'Tu monedero',
  pozoSinMonedero: 'Sin monedero. Conéctalo para poder cobrar.',
  pozoVerificada: '✓ Verificado',
  pozoConectar: '🔗 Conectar monedero',
  pozoQuitar: 'Quitar',
  pozoPasoPayload: 'Pidiendo el permiso…',
  pozoPasoAbriendo: 'Abriendo tu monedero…',
  pozoPasoEsperando: 'Firma en tu monedero y vuelve aquí',
  pozoPasoVerificando: 'Comprobando la firma…',
  pozoFallo: 'No pude verificar el monedero. Inténtalo otra vez.',
  pozoLetraPequena: 'El reparto se hace a mano cada semana. Conectar el monedero NO da acceso a tus fondos: solo firma un mensaje que demuestra que la dirección es tuya.',
  pozoVolver: 'Volver',
};
